/**
 * MiniPlayerBar — the content row from MiniPlayer, extracted as a reusable
 * component. Used everywhere a compact audio player is needed (list rows,
 * detail pages, trash, etc.).
 *
 * Renders exactly the same visual as the floating MiniPlayer bar:
 *  • Animated mini waveform circle
 *  • Title + subtitle text
 *  • Cyan play/pause button (36×36)
 *  • Next button (optional, 32×32) — hidden via showNextButton={false}
 *  • Dismiss/X button (optional, 28×28)
 *  • Thin progress bar at the top edge
 *
 * The floating MiniPlayer keeps its own dismiss + next buttons; replacement
 * instances pass showNextButton={false} and showDismissButton={false}.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, SkipForward, X, Heart, CheckCircle } from 'lucide-react';
import { AnimatedEqualizer } from '@/components/AnimatedEqualizer';

// ─── MiniPlayerBar ────────────────────────────────────────────────────────────

export interface MiniPlayerBarProps {
  /** Track title displayed in the first line */
  title: string;
  /** Subtitle displayed below the title (category, artist, etc.) */
  subtitle?: string;
  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** 0–100 progress percentage for the thin top bar */
  progress?: number;
  /** Called when the play/pause button is clicked */
  onPlayPause: (e: React.MouseEvent) => void;
  /** Called when the Next button is clicked. Required when showNextButton is true. */
  onNext?: (e: React.MouseEvent) => void;
  /** Called when the Dismiss (X) button is clicked. Required when showDismissButton is true. */
  onDismiss?: (e: React.MouseEvent) => void;
  /**
   * Whether to show the next-track (SkipForward) button.
   * Defaults to true (original floating MiniPlayer behaviour).
   * Pass false for replacement instances.
   */
  showNextButton?: boolean;
  /**
   * Whether to show the dismiss (X) button.
   * Defaults to true (original floating MiniPlayer behaviour).
   * Pass false for replacement instances that have their own dismiss mechanism.
   */
  showDismissButton?: boolean;
  /**
   * When true, tapping the bar itself triggers a callback.
   * Used by the floating MiniPlayer to open the full-screen player.
   */
  onBarClick?: () => void;
  /**
   * Whether to show the favorite (heart) button between Play and Next.
   * Defaults to false — only shown for non-floating instances that wire
   * up onToggleFavorite + isFavorited.
   */
  showFavoriteButton?: boolean;
  /** Whether the track is currently favorited (controls heart fill). */
  isFavorited?: boolean;
  /** Called when the favorite (heart) button is clicked. */
  onToggleFavorite?: (e: React.MouseEvent) => void;
  /**
   * Visual variant for the favorite button.
   * 'heart' (default) — purple heart, fills when favorited.
   * 'check' — green CheckCircle badge matching the Live/Wallpaper verified badge.
   */
  favoriteVariant?: 'heart' | 'check';
  /**
   * Accent color for the left-side waveform circle (AnimatedEqualizer).
   * Defaults to cyan when not provided.
   */
  equalizerColor?: string;
  /**
   * Replaces the entire right-side control cluster (play/favorite/next/dismiss)
   * with custom content, leaving the rest of the bar untouched. Used by
   * selection mode, where the row must stay a player but swap its buttons for
   * a single selection checkmark.
   */
  controlsOverride?: React.ReactNode;
}

export function MiniPlayerBar({
  title,
  subtitle,
  isPlaying,
  progress = 0,
  onPlayPause,
  onNext,
  onDismiss,
  showNextButton = true,
  showDismissButton = true,
  onBarClick,
  showFavoriteButton = false,
  isFavorited = false,
  onToggleFavorite,
  favoriteVariant = 'heart',
  equalizerColor,
  controlsOverride,
}: MiniPlayerBarProps) {
  return (
    <div
      onClick={onBarClick}
      style={{
        borderRadius: 18,
        overflow: 'hidden',
        background: isPlaying
          ? '#12122a'
          : 'transparent',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1.5px solid transparent',
        boxShadow: isPlaying
          ? '0 8px 32px rgba(0,0,0,0.55)'
          : '0 8px 32px rgba(0,0,0,0.4)',
        transition: 'background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
        cursor: onBarClick ? 'pointer' : 'default',
      }}
    >
      {/* Progress bar at top — uses scaleX (GPU-composited) instead of width */}
      <div
        style={{
          height: 3,
          background: 'rgba(255,255,255,0.08)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '100%',
            background: 'linear-gradient(90deg, #a855f7 0%, #00e5ff 100%)',
            transform: `scaleX(${Math.max(0, Math.min(100, progress)) / 100})`,
            transformOrigin: 'left center',
            transition: 'transform 0.1s linear',
            willChange: 'transform',
          }}
        />
      </div>

      {/* Content row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
        }}
      >
        {/* Animated equalizer / static waveform circle */}
        <AnimatedEqualizer isPlaying={isPlaying} color={equalizerColor} />

        {/* Title + subtitle */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: isPlaying ? '#ffffff' : '#f0f0f5',
              fontFamily: "'Syne', sans-serif",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.01em',
              transition: 'color 0.25s ease',
            }}
          >
            {title}
          </span>
          {subtitle && (
            <span
              style={{
                fontSize: 11,
                color: isPlaying ? 'hsla(262, 83%, 80%, 0.75)' : 'rgba(240,240,245,0.4)',
                fontFamily: "'Syne', sans-serif",
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transition: 'color 0.25s ease',
              }}
            >
              {subtitle}
            </span>
          )}
        </div>

        {/* Controls */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            flexShrink: 0,
          }}
        >
          {controlsOverride ?? <>
          {/* Play/Pause */}
          <motion.button
            whileTap={{ scale: 0.88 }}
            onClick={(e) => { e.stopPropagation(); onPlayPause(e); }}
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00e5ff 0%, #22d3ee 100%)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: isPlaying
                ? '0 0 8px rgba(0,229,255,0.5), 0 0 18px rgba(0,229,255,0.25)'
                : 'none',
              transition: 'box-shadow 0.3s ease',
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying
              ? <Pause size={14} color="#08070f" fill="#08070f" />
              : <Play size={14} color="#08070f" fill="#08070f" style={{ marginLeft: 2 }} />}
          </motion.button>

          {/* Favorite (optional) — sits right after Play */}
          {showFavoriteButton && favoriteVariant === 'check' && (
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(e); }}
              style={{
                width: 32,
                height: 32,
                marginLeft: 4,
                borderRadius: '50%',
                background: 'rgba(10,10,18,0.7)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1.5px solid rgba(52,211,153,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="In library"
            >
              <CheckCircle size={14} color="#34d399" strokeWidth={2.2} />
            </motion.button>
          )}
          {showFavoriteButton && favoriteVariant === 'heart' && (
            <motion.button
              whileTap={{ scale: 0.85 }}
              onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(e); }}
              style={{
                width: 32,
                height: 32,
                marginLeft: 4,
                borderRadius: '50%',
                background: isFavorited
                  ? 'hsla(262, 83%, 66%, 0.18)'
                  : 'rgba(255,255,255,0.06)',
                border: isFavorited
                  ? '1px solid hsla(262, 83%, 66%, 0.5)'
                  : '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: isFavorited ? 'hsl(262, 83%, 66%)' : 'rgba(240,240,245,0.65)',
                transition: 'background 0.18s ease, border-color 0.18s ease, color 0.18s ease',
              }}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Heart
                size={14}
                strokeWidth={2.2}
                fill={isFavorited ? 'hsl(262, 83%, 66%)' : 'none'}
              />
            </motion.button>
          )}

          {/* Next (optional) */}
          {showNextButton && (
            <motion.button
              whileTap={{ scale: 0.88 }}
              onClick={(e) => { e.stopPropagation(); onNext?.(e); }}
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(240,240,245,0.65)',
              }}
              aria-label="Next"
            >
              <SkipForward size={16} strokeWidth={2} />
            </motion.button>
          )}

          {/* Dismiss (optional) */}
          {showDismissButton && (
            <motion.button
              whileTap={{ scale: 0.88 }}
              onClick={(e) => { e.stopPropagation(); onDismiss?.(e); }}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'rgba(240,240,245,0.45)',
              }}
              aria-label="Dismiss"
            >
              <X size={13} strokeWidth={2.5} />
            </motion.button>
          )}
          </>}
        </div>
      </div>
    </div>
  );
}

export default MiniPlayerBar;
